// UI strings for ShareGate's Delegated Reviews app. Its readers are reviewers: employees whose IT admin
// asked them to review the Microsoft 365 workspaces (Teams, SharePoint sites) they own, e.g. sharing and membership.
// Translate only the string values. Keep every key, every {{placeholder}} and every <tag>…</tag> exactly as written.
// "ShareGate" and "Microsoft" are product names and stay untranslated. Use a friendly, plain, non-technical tone.
export const en = {
    // App frame shown around every page.
    ReviewsLayout: {
        // Screen-reader-only label of the page-loading spinner.
        loadingPage: "Loading page…"
    },
    // Sign-out button in the header.
    LogoutButton: {
        // Keep it very short.
        label: "Log out",
        // {{email}} is the signed-in reviewer's email address.
        tooltip: "Log out from {{email}}"
    },
    // Sign-in page, shown when the reviewer opens the app signed out.
    LandingPage: {
        title: "Sign in to see what needs your attention"
    },
    // Button on the sign-in page that signs in with a Microsoft work account.
    LoginButton: {
        label: "Continue in Microsoft"
    },
    // Page that opens from the link in the reviewer's notification email and sends them to sign-in.
    NotificationPage: {
        // Screen-reader-only label of the redirect spinner.
        redirecting: "Redirecting…"
    },
    // Page shown when a review link belongs to another data region than this site.
    UnavailablePage: {
        // Reassuring: the reviewer did nothing wrong.
        title: "You're in the right place, just with the wrong link.",
        description:
            "Since review links are tied to your organization's region, you'll need to use the one you received in your notification email."
    },
    // 404 page, shown for a link to a page that doesn't exist.
    ReviewsNotFoundError: {
        title: "We couldn't find that page",
        // <supportLink> wraps the words for "contact support", which link to ShareGate's support page.
        description:
            "The page you're looking for doesn't exist or may have moved. If you keep seeing this, <supportLink>contact support</supportLink>."
    },
    // Generic error page, shown when a page fails to load.
    ReviewsInternalServerError: {
        title: "Something went wrong on our end",
        // <supportLink> wraps the words for "contact support", which link to ShareGate's support page.
        description:
            "We hit an unexpected problem loading this page. Try again in a moment. If you keep seeing this, <supportLink>contact support</supportLink>."
    },
    // Strings shared by both error pages.
    Errors: {
        // Button back to the app's start page.
        returnToReviews: "Return to your reviews"
    },
    // Files step of a review: one file at a time, its details and a Keep / Archive / Delete choice.
    FilesStep: {
        // Page heading of the step.
        title: "Clean up files you may no longer need",
        description: "Old files drive up storage costs and risk, and can lead Copilot to surface outdated information.",
        // Short name of the step in the step navigation.
        stepLabel: "File cleanup",
        unavailable: {
            heading: "Files could not be loaded",
            description: "Files are not available for this workspace."
        },
        empty: {
            heading: "No files found",
            description: "No files to review right now"
        },
        loadError: {
            heading: "We were unable to load data",
            description: "It's not you, it's us. Try refreshing the page or come back a little later."
        },
        // Error toast shown when a decision fails to save.
        saveError: "Something went wrong. Please try again.",
        // Above the file details. {{position}} is the shown file's number, {{total}} the number of files.
        filePosition: "File {{position}} of {{total}}",
        // Replaces the file position once every file has a decision.
        allReviewedLabel: "All files reviewed",
        // Screen-reader-only labels of the arrow buttons that go to the previous and next file.
        previousFile: "Previous file",
        nextFile: "Next file",
        // Button that opens the file in Microsoft 365, in a new tab.
        openFile: "Open file",
        // Shown under the decision buttons.
        archiveHelper:
            "Not sure? Archive it. You can restore archived files yourself, and IT can recover deleted ones.",
        // Short uppercase labels above each file property.
        properties: {
            size: "Size",
            createdBy: "Created by",
            lastModified: "Last modified",
            createdDate: "Created date"
        },
        // Tags saying why the file is up for review.
        categories: {
            large: "Over 5 GB",
            stale: "Not edited in over a year",
            old: "Over 3 years old"
        },
        // Card shown once every file has a decision. {{count}} is the number of files.
        allReviewed: {
            heading_one: "{{count}} file reviewed",
            heading_other: "All {{count}} files reviewed",
            description: "Submit when you're ready. You can still change any decision from the list."
        }
    },
    // List of the review's files beside the file details, with a progress bar.
    FileList: {
        title: "Files in this review",
        // Next to the progress bar. {{done}} is the number of files with a decision, {{total}} the number of files.
        done: "{{done}} of {{total}} done",
        // Screen-reader-only label of the list while it loads.
        loading: "Loading files"
    },
    // Menu that applies one decision to every file of the review at once.
    SetAllFilesMenu: {
        // Button that opens the menu. Keep it short.
        label: "Set all",
        replacesNote: "This replaces any choices you already made.",
        keep: "Keep all",
        archive: "Archive all",
        delete: "Delete all",
        // Under "Archive all", when some files can't be archived. {{count}} is the number of those files.
        archiveSkipped_one: "{{count}} file can't be archived",
        archiveSkipped_other: "{{count}} files can't be archived"
    },
    // Three large buttons under the file details: the reviewer's decision for the file.
    FileDecisionPicker: {
        // Screen-reader-only label of the group of buttons.
        label: "File decision",
        keep: "Keep",
        archive: "Archive",
        delete: "Delete"
    },
    // One file in the list beside the file details.
    FileCard: {
        // Screen-reader-only labels of the icon showing the file's decision.
        decisions: {
            undecided: "Undecided",
            keep: "Kept",
            archive: "Archived",
            delete: "Deleted"
        },
        // Screen-reader-only label of a placeholder card while the list loads.
        loading: "Loading"
    },
    // File results in the review summary, before and after the reviewer submits.
    FilesSummary: {
        label: "Files cleaned up",
        // {{formattedCount}} is the number of files.
        outcomes: {
            keep_one: "{{formattedCount}} file will be kept",
            keep_other: "{{formattedCount}} files will be kept",
            archive_one: "{{formattedCount}} file will be archived",
            archive_other: "{{formattedCount}} files will be archived",
            delete_one: "{{formattedCount}} file will be deleted",
            delete_other: "{{formattedCount}} files will be deleted",
            // {{size}} is a storage size, e.g. "2.5 GB".
            archiveNote: "{{size}} freed",
            deleteNote: "{{size}} reclaimed"
        },
        // Running count beside the step navigation, joined with " · ", e.g. "3 kept · 2 to delete".
        // {{formattedCount}} is the number of files.
        tally: {
            keep_one: "{{formattedCount}} kept",
            keep_other: "{{formattedCount}} kept",
            archive_one: "{{formattedCount}} to archive",
            archive_other: "{{formattedCount}} to archive",
            delete_one: "{{formattedCount}} to delete",
            delete_other: "{{formattedCount}} to delete"
        }
    },
    // Tables listing a workspace's users or sharing links.
    ODataReport: {
        loadError: {
            message: "We were unable to load data",
            details: "It's not you, it's us. Try refreshing the page or come back a little later."
        },
        // Screen-reader-only label of the table while it loads.
        loading: "Loading table"
    },
    // Names of the items a table lists.
    ReviewerEntities: {
        users: {
            // The table's name, read by screen readers and used in its page navigation label.
            title: "Users",
            // After the item count above the table, e.g. "12 users".
            lowercase_one: "user",
            lowercase_other: "users"
        },
        sharedLinks: {
            // The table's name, read by screen readers and used in its page navigation label.
            title: "Sharing Links",
            // After the item count above the table, e.g. "12 sharing links".
            lowercase_one: "sharing link",
            lowercase_other: "sharing links"
        }
    },
    // Start page listing the workspaces the reviewer still has to review.
    TasksPage: {
        // Page heading. {{name}} is the reviewer's display name.
        greeting: "Hello, {{name}}",
        intro: "Take a few minutes to review your workspaces and make sure access and content are still up to date.",
        // Screen-reader-only label of the page navigation under the list.
        paginationLabel: "Review tasks pagination",
        // Shown instead of the list once every review is submitted.
        allDone: {
            // {{name}} is the reviewer's display name.
            title: "You're all done, {{name}}.",
            description:
                "Your IT admin will take over from here and would like to thank you for your help. You can safely close this window."
        }
    },
    // Sentence under the start page heading. <emphasis> wraps the words shown in a stronger color: keep both tags.
    DaysUntilDue: {
        message_one: "You have <emphasis>{{count}} day from now to submit your review</emphasis>.",
        message_other: "You have <emphasis>{{count}} days from now to submit your review</emphasis>."
    },
    // List of workspace cards on the start page, grouped by due date.
    TaskList: {
        // Small uppercase label above the list. {{count}} is the number of workspaces left to review.
        toReview: "{{count}} to review",
        // Small uppercase label above each group of cards. {{date}} is a formatted date, e.g. "March 3, 2027".
        dueOn: "Due {{date}}",
        // Screen-reader-only name of a group of cards. {{date}} is a formatted date.
        tasksDueOn: "Tasks due {{date}}",
        startReview: "Start review",
        continueReview: "Continue review",
        // Screen-reader-only name of a card. {{workspace}} is the workspace's name.
        startReviewFor: "Start review for {{workspace}}",
        // Screen-reader-only name of a card. {{workspace}} is the workspace's name.
        continueReviewFor: "Continue review for {{workspace}}",
        reviewInProgress: "Review in progress"
    },
    // Small tags next to a workspace's name: how long it has been inactive and how many people it has.
    WorkspaceMetadata: {
        // Screen-reader-only name of the tag group.
        membershipLabel: "Workspace membership",
        owners_one: "{{count}} owner",
        owners_other: "{{count}} owners",
        members_one: "{{count}} member",
        members_other: "{{count}} members",
        guests_one: "{{count}} guest",
        guests_other: "{{count}} guests",
        // Last line of the tooltip listing the people, when the list is cut short.
        more_one: "+ {{count}} more",
        more_other: "+ {{count}} more",
        inactiveDays_one: "Inactive for {{count}} day",
        inactiveDays_other: "Inactive for {{count}} days",
        inactiveMonths_one: "Inactive for {{count}} month",
        inactiveMonths_other: "Inactive for {{count}} months",
        inactiveYears_one: "Inactive for {{count}} year",
        inactiveYears_other: "Inactive for {{count}} years"
    },
    // Frame of the step-by-step review of one workspace: the heading and the footer with the step navigation.
    TaskStepsPage: {
        // Heading of the workspace lifecycle step, where the reviewer keeps, archives or deletes the workspace.
        lifecycleTitle: "What do you want to do with this space?",
        // Name of the lifecycle step in the step navigation. Keep it short.
        lifecycleStepLabel: "Workspace lifecycle",
        // Tooltip on the workspace's name, which opens the workspace in a new browser tab.
        openInNewTab: "Open in new tab",
        back: "Back",
        saveAndExit: "Save and exit",
        next: "Next",
        submit: "Complete review",
        // Pop-up notifications.
        toasts: {
            submitted: "Your review has been submitted successfully.",
            error: "Something went wrong. Please try again.",
            alreadyCompleted: "This review has already been completed.",
            closedByAdmin: "This review has been closed by your IT admin."
        }
    },
    // Text beside the Next and Submit buttons that sums up the reviewer's decisions on the current step.
    DecisionTally: {
        // Shown while the reviewer has made no decision on the step.
        noChanges: "No changes",
        // Joined with " · ", e.g. "3 revoked · 5 recipients removed". {{formattedCount}} is the number of sharing links revoked or of recipients removed.
        // TODO: confirm "recipients removed" copy with content design.
        sharingLinks: {
            revoked_one: "{{formattedCount}} revoked",
            revoked_other: "{{formattedCount}} revoked",
            recipientsRemoved_one: "{{formattedCount}} recipient removed",
            recipientsRemoved_other: "{{formattedCount}} recipients removed"
        },
        // {{formattedCount}} is the number of members or guests.
        membership: {
            removed_one: "{{formattedCount}} removed",
            removed_other: "{{formattedCount}} removed"
        }
    },
    // Step navigation in the footer of the review.
    ReviewSteps: {
        // Screen-reader-only name of the list of steps.
        label: "Review steps"
    },
    // Dialog that asks the reviewer to confirm before their answers are submitted.
    SubmissionConfirmModal: {
        cancel: "Cancel",
        enriched: {
            title: "One last look before you finish",
            confirm: "Submit review"
        },
        summary: {
            title: "This is what happens when you confirm.",
            confirm: "Confirm review",
            // Card in the dialog, shown when the reviewer chose to keep the workspace.
            workspaceStaysActive: "Workspace stays active",
            // Caption under a number, in a card of the dialog.
            linkRecipientsRemoved: "Link recipients removed",
            warning: "Actions take effect once you confirm this workspace review."
        }
    },
    // List of what will change, in the confirmation dialog before submitting.
    SubmissionOutcomeList: {
        nothingChanges: "You confirmed everything is up to date.",
        noChanges: "No changes",
        loadError: "We were unable to load your review summary. You can still submit your review.",
        workspaceStaysActive: "Workspace will stay active",
        // {{formattedCount}} is the number of people removed from shared links.
        recipientsRemoved_one: "{{formattedCount}} recipient will be removed",
        recipientsRemoved_other: "{{formattedCount}} recipients will be removed"
    },
    // Card that filters a step's list, e.g. "12 / Links shared with anyone".
    FilterCardItem: {
        // Link-styled text on a card that is not selected yet. Keep it very short.
        show: "Show",
        // Screen-reader-only name of the card. {{label}} is the card's caption, {{count}} its number.
        countLabel: "{{label}}: {{count}}"
    },
    FilterCardsSkeleton: {
        // Screen-reader-only name of the row of filter cards while they load.
        label: "Quick filters"
    },
    // Button on a row of a step's list, once the reviewer has chosen an action for that row.
    RowActionButton: {
        undo: "Undo"
    },
    // Workspace lifecycle step: one card per action (keep, archive, delete). Picking a card asks why, with a set of reason tags.
    LifecycleStep: {
        // Screen-reader-only aria-label on the list of action cards; never visible on screen.
        actionsLabel: "Lifecycle actions",
        // Small tag on the action card that matches how recently the workspace was used.
        recommended: "Recommended",
        // Screen-reader-only aria-label on the group of reason tags; never visible on screen.
        reasonLabel: "Reason",
        // Label of the free-text field shown when the reviewer picks the "Other" reason.
        noteLabel: "Note",
        notePlaceholder: "Add details to support your decision…",
        // Error toast when saving the reviewer's choice fails.
        saveError: "Something went wrong. Please try again.",
        keepWorkspace: {
            // Card title. Short imperative: "it" is the workspace.
            label: "Keep it",
            description: "Still actively used",
            reasonQuestion: "Why are you keeping it?",
            // Tooltip on the "Recommended" tag of this card.
            recommendedTooltip: "Active within the last 6 months"
        },
        archiveWorkspace: {
            // Card title. Short imperative: "it" is the workspace.
            label: "Archive it",
            description:
                "Archived content will no longer be accessible to you or other users, but can be restored by an IT admin at any time.",
            reasonQuestion: "Why are you archiving it?",
            // Tooltip on the "Recommended" tag of this card.
            recommendedTooltip: "Inactive for 6 to 12 months"
        },
        deleteWorkspace: {
            // Card title. Short imperative: "it" is the workspace.
            label: "Delete it",
            description:
                "This workspace and all its content will be permanently deleted. An IT admin will have 30 days to restore it if you change your mind.",
            reasonQuestion: "Why are you deleting it?",
            // Tooltip on the "Recommended" tag of this card.
            recommendedTooltip: "Inactive for over 1 year"
        },
        // Reason tags the reviewer picks to explain their choice. Short, sentence case.
        reasons: {
            unknown: "Unknown",
            notProvided: "Not provided",
            other: "Other",
            noLongerNeeded: "No longer needed",
            securityRisk: "Security risk",
            leftOrganization: "Left organization",
            excessivePermissions: "Excessive permissions",
            sharedInError: "Shared in error",
            stillActivelyUsed: "Still actively used",
            neededForReference: "Needed for reference",
            sharedWithExternalPartners: "Shared with external partners",
            noLongerUsed: "No longer used",
            projectOrInitiativeEnded: "Project or initiative ended",
            replacedWithSomethingElse: "Replaced with something else",
            duplicateWorkspace: "Duplicate workspace",
            createdInError: "Created in error"
        }
    },
    // Confirmation dialog shown when the reviewer submits a review that archives or deletes the workspace.
    LifecycleConfirmation: {
        archiveWorkspace: {
            title: "Archive this workspace?",
            // Label of the dialog's confirm button. One word.
            confirmLabel: "Archive",
            content:
                "If you archive this workspace, its content will no longer be accessible but can be restored by an IT admin at any time."
        },
        deleteWorkspace: {
            title: "Delete this workspace?",
            // Label of the dialog's confirm button. One word.
            confirmLabel: "Delete",
            content:
                "If you delete this workspace, all its content will be permanently deleted. An IT admin will have 30 days to restore it."
        }
    },
    // Workspace membership step: a table of the workspace's members the reviewer can revoke access for.
    MembershipStep: {
        // Screen-reader-only aria-label on the row of quick filter cards; never visible on screen.
        quickFiltersLabel: "Quick filters",
        // Button that revokes the selected members', or one member's, access to the workspace.
        revokeAccess: "Revoke access",
        // Screen-reader-only aria-label on the switch between member types; never visible on screen.
        userTypeFiltersLabel: "User type filters",
        // Options of the switch above the members table.
        segments: {
            all: "All",
            internal: "Internal members",
            guests: "External guests"
        },
        // Quick filter cards: each counts the members it describes.
        filters: {
            accountDeactivated: "Account deactivated",
            inactive: "Haven't signed in for 6+ months",
            onlyInThisWorkspace: "Not part of any other workspace"
        },
        // Shown in place of the step when the workspace's members can't be loaded.
        unavailable: {
            title: "Members could not be loaded",
            description: "Members are not available for this workspace."
        },
        // The step reviews guests only: people from outside the organization.
        guests: {
            // Step heading.
            title: "Remove guests who no longer need access",
            // Step name in the list of review steps. Short.
            stepLabel: "Guest members",
            emptyTitle: "No guests found",
            emptyDescription: "No external guests to review right now",
            // Row title in the review summary.
            summaryLabel: "Guest access removed",
            // Row in the review summary. {{formattedCount}} is the number of guests, already formatted.
            summaryOutcome_one: "{{formattedCount}} guest will be removed",
            summaryOutcome_other: "{{formattedCount}} guests will be removed"
        },
        // The step reviews internal members only: people from the organization.
        internal: {
            // Step heading.
            title: "Remove internal members who no longer need access",
            // Step name in the list of review steps. Short.
            stepLabel: "Internal members",
            emptyTitle: "No internal members found",
            emptyDescription: "No internal members to review right now",
            // Row title in the review summary.
            summaryLabel: "Internal access removed",
            // Row in the review summary. {{formattedCount}} is the number of members, already formatted.
            summaryOutcome_one: "{{formattedCount}} internal member will be removed",
            summaryOutcome_other: "{{formattedCount}} internal members will be removed"
        },
        // The step reviews both guests and internal members.
        all: {
            // Step heading.
            title: "Remove members who no longer need access",
            // Step name in the list of review steps. Short.
            stepLabel: "Group membership",
            emptyTitle: "No members found",
            emptyDescription: "No members to review right now",
            // Row title in the review summary.
            summaryLabel: "Member access removed",
            // Row in the review summary. {{formattedCount}} is the number of members, already formatted.
            summaryOutcome_one: "{{formattedCount}} member will be removed",
            summaryOutcome_other: "{{formattedCount}} members will be removed"
        }
    },
    // Sharing links step: a table of the links that share the workspace's files, which the reviewer can revoke.
    SharingLinksStep: {
        // Screen-reader-only aria-label on the row of quick filter cards; never visible on screen.
        quickFiltersLabel: "Quick filters",
        // Button that revokes the selected links, or one link.
        revoke: "Revoke",
        // Screen-reader-only aria-label on the spinner of a link action; never visible on screen.
        loading: "Loading",
        // Screen-reader-only aria-label on the pair of action buttons of a link; never visible on screen.
        linkActionsLabel: "Link actions",
        // Screen-reader-only aria-label on the switch between link audiences; never visible on screen.
        linkAudienceFiltersLabel: "Link audience filters",
        // Icon button tooltips on a link: remove some of the people it's shared with, or delete the link.
        linkActions: {
            manageAccess: "Manage access",
            deleteLink: "Delete link"
        },
        // Options of the switch above the links table: links shared outside or inside the organization.
        segments: {
            all: "All",
            external: "External",
            internal: "Internal"
        },
        // Quick filter cards: each counts the links it describes, with a tooltip that suggests what to do.
        filters: {
            anyoneCanEdit: "Anyone can edit files",
            anyoneCanEditTooltip: "These are worth a quick check, is that level of access still needed?",
            accessDoesNotExpire: "Access does not expire",
            accessDoesNotExpireTooltip: "Review these links to confirm whether indefinite access is still required.",
            sharedByLeaver: "Shared by someone who left",
            sharedByLeaverTooltip: "Links may no longer be actively managed. Consider revoking them."
        },
        // Shown in place of the step when the workspace's links can't be loaded.
        unavailable: {
            title: "Shared links could not be loaded",
            description: "Shared links are not available for this workspace."
        },
        // The step reviews links shared outside the organization only.
        external: {
            // Step heading.
            title: "Control who can access your files externally",
            // Step name in the list of review steps. Short.
            stepLabel: "External links",
            emptyTitle: "No external links found",
            emptyDescription: "No external links to review right now",
            // Row title in the review summary.
            summaryLabel: "External links revoked",
            // Row in the review summary. {{formattedCount}} is the number of links, already formatted.
            summaryOutcome_one: "{{formattedCount}} external link will be revoked",
            summaryOutcome_other: "{{formattedCount}} external links will be revoked"
        },
        // The step reviews links shared inside the organization only.
        internal: {
            // Step heading.
            title: "Control who can access your files internally",
            // Step name in the list of review steps. Short.
            stepLabel: "Internal links",
            emptyTitle: "No internal links found",
            emptyDescription: "No internal links to review right now",
            // Row title in the review summary.
            summaryLabel: "Internal links revoked",
            // Row in the review summary. {{formattedCount}} is the number of links, already formatted.
            summaryOutcome_one: "{{formattedCount}} internal link will be revoked",
            summaryOutcome_other: "{{formattedCount}} internal links will be revoked"
        },
        // The step reviews every sharing link.
        all: {
            // Step heading.
            title: "Control who can access your files",
            // Step name in the list of review steps. Short.
            stepLabel: "Sharing links",
            emptyTitle: "No sharing links found",
            emptyDescription: "No sharing links to review right now",
            // Row title in the review summary.
            summaryLabel: "Sharing links revoked",
            // Row in the review summary. {{formattedCount}} is the number of links, already formatted.
            summaryOutcome_one: "{{formattedCount}} sharing link will be revoked",
            summaryOutcome_other: "{{formattedCount}} sharing links will be revoked"
        }
    },
    // Dialog where the reviewer picks people to remove from a link shared with specific people.
    LinkRecipientsModal: {
        title: "Remove access",
        description:
            "Pick who should lose access. The link stays for everyone else. To remove access for everyone, delete the link instead.",
        // Label of the save button when the reviewer unselects everyone they had picked before.
        keepAll: "Keep all recipients",
        // Error toast when saving the reviewer's choice fails.
        saveError: "Something went wrong. Please try again."
    }
} as const;
